import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import Loading from "components/shared-components/Loading";
import MobileNav from "components/layout-components/MobileNav";
import HeaderNav from "components/layout-components/HeaderNav";
import PageHeader from "components/layout-components/PageHeader";
import Footer from "components/layout-components/Footer";
import AppViews from "views/app-views";
import { Grid, Layout } from "antd";

import navigationConfig from "configs/NavigationConfig";
import {
  DIR_LTR,
  DIR_RTL,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
  SIDE_NAV_COLLAPSED_WIDTH,
  SIDE_NAV_WIDTH,
} from "constants/ThemeConstant";
import utils from "utils";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { useFirestoreConnect } from "react-redux-firebase";
import {
  FIRESTORE_CUSTOMER_GROUPS_TABLE,
  FIRESTORE_DISCOUNT_CODES_TABLE,
  FIRESTORE_NOTIFICATIONS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_REGIONS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../constants/CompanyConstants";
import _ from "lodash";
import moment from "moment";

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({ navCollapsed, navType, location, direction }) => {
  const queryModifications = useSelector((state) => state.queries);
  const initialQueries = [
    {
      collection: FIRESTORE_REGIONS_TABLE,
      doc: COMPANY_COUNTRY_CODE,
      subcollections: [{ collection: FIRESTORE_PRODUCTS_TABLE }],
      storeAs: FIRESTORE_PRODUCTS_TABLE,
    },
    {
      collection: FIRESTORE_USERS_TABLE,
      where: [
        ["userRole", "==", "user"],
        ["userRegion", "==", COMPANY_COUNTRY_CODE],
      ],
      storeAs: "webshopUsers",
    },
    {
      collection: FIRESTORE_REGIONS_TABLE,
      doc: COMPANY_COUNTRY_CODE,
      subcollections: [{ collection: FIRESTORE_NOTIFICATIONS_TABLE }],
      storeAs: FIRESTORE_NOTIFICATIONS_TABLE,
    },
    {
      collection: FIRESTORE_REGIONS_TABLE,
      doc: COMPANY_COUNTRY_CODE,
      subcollections: [{ collection: FIRESTORE_CUSTOMER_GROUPS_TABLE }],
      storeAs: FIRESTORE_CUSTOMER_GROUPS_TABLE,
    },
    {
      collection: FIRESTORE_REGIONS_TABLE,
      doc: COMPANY_COUNTRY_CODE,
      subcollections: [{ collection: FIRESTORE_DISCOUNT_CODES_TABLE }],
      storeAs: FIRESTORE_DISCOUNT_CODES_TABLE,
    },
  ];
  const [allQueries, setAllQueries] = useState(initialQueries);

  // Fetch all collections from Firestore and put in redux store
  useFirestoreConnect(allQueries);

  useEffect(() => {
    if (!!queryModifications) {
      const queryClone = _.clone(allQueries);
      if (!!queryModifications.ordersMaxDaysAgo) {
        let orderDaysAgoTimestamp = moment().subtract(queryModifications.ordersMaxDaysAgo, "days").unix();
        // Turn into milliseconds
        orderDaysAgoTimestamp = orderDaysAgoTimestamp * 1000;
        queryClone.push({
          collection: FIRESTORE_REGIONS_TABLE,
          doc: COMPANY_COUNTRY_CODE,
          subcollections: [
            { collection: FIRESTORE_ORDERS_TABLE, where: ["orderDateTime", ">=", orderDaysAgoTimestamp] },
          ],
          storeAs: FIRESTORE_ORDERS_TABLE,
        });
        setAllQueries(queryClone);
      } else {
        _.remove(
          queryClone,
          (item) =>
            item.collection === FIRESTORE_REGIONS_TABLE &&
            item.doc === COMPANY_COUNTRY_CODE &&
            item.subcollections[0].collection === FIRESTORE_ORDERS_TABLE
        );
        queryClone.push({
          collection: FIRESTORE_REGIONS_TABLE,
          doc: COMPANY_COUNTRY_CODE,
          subcollections: [{ collection: FIRESTORE_ORDERS_TABLE }],
          storeAs: FIRESTORE_ORDERS_TABLE,
        });
        setAllQueries(queryClone);
      }
    }
  }, [queryModifications]);

  const currentRouteInfo = utils.getRouteInfo(navigationConfig, location.pathname);
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;
  const getLayoutGutter = () => {
    if (isNavTop || isMobile) {
      return 0;
    }
    return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  };

  const { status } = useThemeSwitcher();

  if (status === "loading") {
    return <Loading cover="page" />;
  }

  const getLayoutDirectionGutter = () => {
    if (direction === DIR_LTR) {
      return { paddingLeft: getLayoutGutter() };
    }
    if (direction === DIR_RTL) {
      return { paddingRight: getLayoutGutter() };
    }
    return { paddingLeft: getLayoutGutter() };
  };

  return (
    <Layout>
      <HeaderNav isMobile={isMobile} />
      {isNavTop && !isMobile ? <TopNav routeInfo={currentRouteInfo} /> : null}
      <Layout className="app-container">
        {isNavSide && !isMobile ? <SideNav routeInfo={currentRouteInfo} /> : null}
        <Layout className="app-layout" style={getLayoutDirectionGutter()}>
          <div className={`app-content ${isNavTop ? "layout-top-nav" : ""}`}>
            <PageHeader display={currentRouteInfo?.breadcrumb} title={currentRouteInfo?.title} />
            <Content>
              <AppViews />
            </Content>
          </div>
          <Footer />
        </Layout>
      </Layout>
      {isMobile && <MobileNav />}
    </Layout>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
